import { Badge, Button, Code, Drawer, Group, NumberInput, Select, SimpleGrid, Stack, Table, Text } from "@mantine/core";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { runs, useCancelRun, useMaskOverrides, useProcessPartial, useSweeps } from "../../api/generated/stockvol";
import type { PageRunOut, PageSweepOut, RunOut } from "../../api/generated/model";
import { useSiteInfo } from "../../api/hooks";
import { RequireRole } from "../../auth";
import { act, confirm } from "../../lib/actions";
import { CopyId, MiniBar } from "../../components/bits";
import {
  IconCircleCheck,
  IconCircleX,
  IconClock,
  IconListDetails,
  IconPlayerPlay,
} from "../../components/icons";
import { PageHeader } from "../../components/PageHeader";
import { Section, TableCard } from "../../components/Section";
import { TableSkeleton } from "../../components/Skeletons";
import { StatCard } from "../../components/StatCard";
import { formatLocal } from "../../lib/time";
import { GRADE_COLOUR, QualityPanel } from "./QualityPanel";

const STATUS_COLOUR: Record<string, string> = {
  succeeded: "teal",
  running: "blue",
  queued: "gray",
  failed: "red",
  cancelled: "gray",
  superseded: "gray",
};

const STATUS_ICON: Record<string, typeof IconCircleCheck> = {
  succeeded: IconCircleCheck,
  running: IconPlayerPlay,
  queued: IconClock,
  failed: IconCircleX,
};

function StatusBadge({ status }: { status: string }) {
  const Ico = STATUS_ICON[status];
  return (
    <Badge
      size="sm"
      variant="light"
      color={STATUS_COLOUR[status] ?? "gray"}
      tt="none"
      leftSection={Ico ? <Ico size={12} /> : undefined}
    >
      {status}
    </Badge>
  );
}

/** Where the run's time went: one bar per stage, longest first. */
function StageTimings({ timings }: { timings: Record<string, number> }) {
  const rows = Object.entries(timings)
    .map(([k, v]) => [k, Number(v)] as const)
    .filter(([, v]) => Number.isFinite(v))
    .sort((a, b) => b[1] - a[1]);
  const max = Math.max(1e-9, ...rows.map(([, v]) => v));
  const total = rows.reduce((a, [, v]) => a + v, 0);
  if (rows.length === 0)
    return (
      <Text size="xs" c="dimmed">
        No timings recorded.
      </Text>
    );
  return (
    <Stack gap={6}>
      {rows.map(([k, v]) => (
        <Group key={k} gap="sm" wrap="nowrap">
          <Text size="xs" w={130} truncate title={k}>
            {k}
          </Text>
          <div style={{ flex: 1 }}>
            <MiniBar frac={v / max} colour="var(--mantine-color-brand-6)" width="100%" />
          </div>
          <Text size="xs" w={70} ta="right" className="sv-num">
            {v.toFixed(1)} s
          </Text>
        </Group>
      ))}
      <Text size="xs" c="dimmed" ta="right">
        total {total.toFixed(1)} s
      </Text>
    </Stack>
  );
}

const ID_ROWS = new Set(["Run", "Sweep", "Config", "Results digest"]);

export function runSeconds(r: RunOut): number | null {
  if (!r.started_at || !r.finished_at) return null;
  return (Date.parse(r.finished_at) - Date.parse(r.started_at)) / 1000;
}

/** Every run, newest first, with its provenance (M6.5). */
export default function RunsPage() {
  const site = useSiteInfo();
  const tz = site.data?.timezone ?? "UTC";
  const q = useInfiniteQuery({
    queryKey: ["runs", "all"],
    queryFn: async ({ pageParam }) =>
      ((await runs({ limit: 50, ...(pageParam ? { cursor: pageParam } : {}) })) as { data: PageRunOut }).data,
    initialPageParam: "",
    getNextPageParam: (last) => last.next ?? undefined,
    refetchInterval: 30_000,
  });
  const [open, setOpen] = useState<RunOut | null>(null);
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const done = items.filter((r) => r.status === "succeeded" || r.status === "failed");
  const ok = done.filter((r) => r.status === "succeeded").length;
  const secs = items.map(runSeconds).filter((v): v is number => v !== null).sort((a, b) => a - b);
  const median = secs.length ? secs[Math.floor(secs.length / 2)]! : null;
  const active = items.filter((r) => r.status === "queued" || r.status === "running").length;
  return (
    <Stack gap="lg">
      <PageHeader title="Runs" subtitle="Every processing run of a recorded sweep, newest first, with its provenance" />
      {q.isPending ? (
        <TableSkeleton rows={10} cols={7} />
      ) : (
        <>
      <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="md">
        <StatCard
          label="Runs loaded"
          value={items.length}
          secondary={`${active} queued or running`}
          icon={<IconListDetails size={20} />}
        />
        <StatCard
          label="Succeeded"
          value={done.length ? `${Math.round((100 * ok) / done.length)} %` : "—"}
          secondary={`${ok} of ${done.length} finished runs`}
          icon={<IconCircleCheck size={20} />}
          tone={ok === done.length ? "ok" : "warning"}
        />
        <StatCard
          label="Median processing time"
          value={median === null ? "—" : median.toFixed(0)}
          unit={median === null ? undefined : "s"}
          secondary={secs.length ? `fastest ${secs[0]!.toFixed(0)} s · slowest ${secs[secs.length - 1]!.toFixed(0)} s` : ""}
          icon={<IconClock size={20} />}
        />
        <StatCard
          label="Latest run"
          value={<span style={{ fontSize: 18 }}>{items[0] ? formatLocal(items[0].queued_at, tz) : "—"}</span>}
          secondary={items[0] ? items[0].status : ""}
          icon={<IconPlayerPlay size={20} />}
        />
      </SimpleGrid>
      <TableCard
        title="Runs"
        footer={
          q.hasNextPage ? (
            <Button variant="subtle" size="xs" onClick={() => void q.fetchNextPage()} loading={q.isFetchingNextPage}>
              Older runs
            </Button>
          ) : undefined
        }
      >
      <Table highlightOnHover verticalSpacing="xs" fz="sm" data-testid="runs-table">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Queued</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Attempt</Table.Th>
            <Table.Th>Grade</Table.Th>
            <Table.Th ta="right">Duration</Table.Th>
            <Table.Th>Algorithm</Table.Th>
            <Table.Th>Config</Table.Th>
            <Table.Th>Floor</Table.Th>
            <Table.Th>Error</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((r) => {
            const sec = runSeconds(r);
            const grade = (r.quality as { grade?: string } | undefined)?.grade;
            return (
              <Table.Tr key={r.id} onClick={() => setOpen(r)} style={{ cursor: "pointer" }}>
                <Table.Td style={{ whiteSpace: "nowrap" }}>{formatLocal(r.queued_at, tz)}</Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <StatusBadge status={r.status} />
                    {r.is_current && (
                      <Badge size="sm" variant="outline" color="brand" tt="none">
                        current
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>{r.attempt}</Table.Td>
                <Table.Td>
                  {grade ? (
                    <Badge size="sm" variant="dot" color={GRADE_COLOUR[grade] ?? "gray"} tt="none">
                      {grade}
                    </Badge>
                  ) : (
                    "—"
                  )}
                </Table.Td>
                <Table.Td ta="right" className="sv-num">
                  {sec === null ? "—" : `${sec.toFixed(0)} s`}
                </Table.Td>
                <Table.Td c="dimmed">{r.algorithm ?? "—"}</Table.Td>
                <Table.Td>
                  <Text ff="monospace" size="xs" c="dimmed">
                    {r.config_sha256?.slice(0, 8) ?? "—"}
                  </Text>
                </Table.Td>
                <Table.Td>{r.floor_version === null ? "—" : `v${r.floor_version}`}</Table.Td>
                <Table.Td>
                  <Text size="xs" c="red" lineClamp={1}>
                    {r.error ?? ""}
                  </Text>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      </TableCard>
      <CutShort tz={tz} />
        </>
      )}
      <Drawer
        opened={open !== null}
        onClose={() => setOpen(null)}
        position="right"
        size="lg"
        title={
          open && (
            <Group gap="sm">
              <Text fw={600}>Run of {formatLocal(open.queued_at, tz)}</Text>
              <StatusBadge status={open.status} />
            </Group>
          )
        }
      >
        {open && (
          <Stack gap="md">
            <QualityPanel quality={open.quality as never} />
            <Section title="Provenance" padding="md">
            <Table fz="xs" verticalSpacing={6}>
              <Table.Tbody>
                {(
                  [
                    ["Run", open.id],
                    ["Sweep", open.sweep_id],
                    ["Algorithm", open.algorithm ?? "—"],
                    ["Config", open.config_sha256 ?? "—"],
                    ["Floor", open.floor_version === null ? "—" : `v${open.floor_version}`],
                    ["Results digest", open.results_digest ?? "—"],
                    ["Directory", open.run_dir ?? "—"],
                  ] as const
                ).map(([k, v]) => (
                  <Table.Tr key={k}>
                    <Table.Td c="dimmed">{k}</Table.Td>
                    <Table.Td>
                      {ID_ROWS.has(k) && v !== "—" ? <CopyId value={v} chars={12} /> : <Code>{v}</Code>}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            </Section>
            <Section title="Stage timings" padding="md">
              <StageTimings timings={(open.stage_timings ?? {}) as Record<string, number>} />
            </Section>
            {open.error && (
              <Code block color="red.1">
                {open.error}
              </Code>
            )}
            <RunActions run={open} onDone={() => setOpen(null)} />
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}

/** Recordings cut short (stopped by an operator, the sensor or the position source lost) that
 * have no results: operators may process them as partial sweeps. */
function CutShort({ tz }: { tz: string }) {
  const qc = useQueryClient();
  const sweeps = useSweeps(
    { limit: 50 },
    { query: { select: (r) => (r as { data: PageSweepOut }).data, refetchInterval: 60_000 } },
  );
  const process = useProcessPartial();
  const rows = (sweeps.data?.items ?? []).filter((w) => w.capture_status !== "complete" && !w.current_run);
  if (rows.length === 0) return null;
  return (
    <RequireRole min="operator">
      <TableCard title="Recordings cut short">
        <Table fz="sm" data-testid="cut-short">
          <Table.Tbody>
            {rows.map((w) => (
              <Table.Tr key={w.id}>
                <Table.Td>{formatLocal(w.started_at, tz)}</Table.Td>
                <Table.Td>{w.capture_status}</Table.Td>
                <Table.Td>{Math.round(100 * w.arc_coverage_frac)} % of the arc</Table.Td>
                <Table.Td>
                  <Button
                    size="compact-xs"
                    variant="light"
                    onClick={() =>
                      confirm("Process as partial", "Process this recording as a partial sweep?", () =>
                        void act(() => process.mutateAsync({ sweepId: w.id }), {
                          done: "Queued",
                          qc,
                          refresh: ["sweeps", "runs"],
                        }),
                      )
                    }
                  >
                    Process as partial
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </TableCard>
    </RequireRole>
  );
}

/** In the run's drawer: cancel a live run (admin); machinery override → a reprocess (operator). */
function RunActions({ run, onDone }: { run: RunOut; onDone: () => void }) {
  const qc = useQueryClient();
  const cancel = useCancelRun();
  const mask = useMaskOverrides();
  const [action, setAction] = useState<string | null>("keep");
  const [x, setX] = useState<number | string>("");
  const [y, setY] = useState<number | string>("");
  const [r, setR] = useState<number | string>(10);
  return (
    <Stack gap="xs">
      {(run.status === "queued" || run.status === "running") && (
        <RequireRole min="admin">
          <Group>
            <Button
              size="xs"
              color="red"
              variant="light"
              loading={cancel.isPending}
              onClick={() =>
                confirm("Cancel the run", "Stop this run? A running one ends at its next check.", () =>
                  void act(() => cancel.mutateAsync({ runId: run.id }), {
                    done: "Cancel requested",
                    qc,
                    refresh: ["runs"],
                  }).then(onDone),
                )
              }
            >
              Cancel run
            </Button>
          </Group>
        </RequireRole>
      )}
      <RequireRole min="operator">
        <Text size="sm" fw={600}>
          Machinery override
        </Text>
        <Text size="xs" c="dimmed">
          A circle in the site frame (metres from the pivot, x east, y north): keep what was masked there as
          material, or mask it. The sweep is processed again with it.
        </Text>
        <Group align="flex-end">
          <Select size="xs" data={["keep", "mask"]} value={action} onChange={setAction} w={90} />
          <NumberInput size="xs" label="x m" value={x} onChange={setX} w={90} />
          <NumberInput size="xs" label="y m" value={y} onChange={setY} w={90} />
          <NumberInput size="xs" label="radius m" value={r} onChange={setR} min={0.5} w={90} />
          <Button
            size="xs"
            variant="light"
            disabled={x === "" || y === ""}
            loading={mask.isPending}
            onClick={() =>
              confirm(
                "Override and reprocess",
                `${action ?? "keep"} within ${r} m of (${x}, ${y}) and process the sweep again?`,
                () =>
                  void act(
                    () =>
                      mask.mutateAsync({
                        sweepId: run.sweep_id,
                        data: {
                          overrides: [
                            {
                              action: (action ?? "keep") as "keep" | "mask",
                              x_m: Number(x),
                              y_m: Number(y),
                              radius_m: Number(r),
                            },
                          ],
                        },
                      }),
                    { done: "Reprocess queued", qc, refresh: ["runs"] },
                  ),
              )
            }
          >
            Apply
          </Button>
        </Group>
      </RequireRole>
    </Stack>
  );
}
